/**
 * @fileoverview Shared custom tooltip components for various chart types
 * @requires react
 * @requires @nivo/line
 */

import React from "react"
import { Point } from "@nivo/line"
import type { NivoLineChartSeries } from "types/ReportingDashboard/timeseries.types"

/**
 * Props for the BarChart CustomTooltip component
 */
export interface BarChartTooltipProps {
  /** The identifier for the data point */
  id: string
  /** The value to display */
  value: number | string
  /** The color of the tooltip */
  color: string
  /** The index value for the data point */
  indexValue: string
}

/**
 * Props for the PieChart CustomTooltip component
 */
export interface PieChartTooltipProps {
  datum: {
    id: string | number
    color: string
    formattedValue: string
    value: number
  }
}

/**
 * Props for the LineChart CustomTooltip component
 */
export interface LineChartTooltipProps {
  point: Point<NivoLineChartSeries>
}

/**
 * Custom tooltip component for bar charts
 */
export const BarChartTooltip: React.FC<BarChartTooltipProps> = ({
  id,
  value,
  color,
  indexValue,
}) => (
  <div className="bg-white p-2 rounded-lg">
    <strong style={{ color: "#333" }}>{indexValue}</strong>
    <br />
    <span style={{ color }}>
      {id}: {value}
    </span>
  </div>
)

/**
 * Custom tooltip component for pie charts
 */
export const PieChartTooltip: React.FC<PieChartTooltipProps> = ({ datum }) => (
  <div className="bg-white p-2 rounded-lg shadow-md">
    <strong style={{ color: "#333" }}>{datum.id}</strong>
    <br />
    <span style={{ color: datum.color }}>
      {datum.formattedValue || datum.value}
    </span>
  </div>
)

/**
 * Custom tooltip component for line charts
 */
export const LineChartTooltip: React.FC<LineChartTooltipProps> = ({
  point,
}) => (
  <div
    style={{
      background: "white",
      padding: "9px 12px",
      border: "1px solid #ccc",
      borderRadius: "3px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      fontSize: "12px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
      <span
        style={{
          display: "inline-block",
          width: "12px",
          height: "12px",
          backgroundColor: point.seriesColor,
          marginRight: "7px",
        }}
      ></span>
      <strong>{point.seriesId}</strong>
    </div>
    <div>
      {point.data.xFormatted}:{" "}
      <strong style={{ color: point.seriesColor }}>
        {point.data.yFormatted}
      </strong>
    </div>
  </div>
)
